import { useEffect, useState } from "react";
import {
  Save,
  ExternalLink,
  Plus,
  EyeOff,
  Settings,
  CreditCard,
  Clock,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MenusService } from "@/services/MenusService";
import { SectionsService } from "@/services/SectionsService";
import { OffersService } from "@/services/OffersService";
import { SectionItemsService } from "@/services/SectionItemsService";
import type { MenusOutput } from "@/interfaces/menus";
import type { SectionsOutput } from "@/interfaces/sections";
import type { OfferProducts, OffersOutput } from "@/interfaces/offers";
import type { ProductsOutput } from "@/interfaces/products";
import type {
  SectionItemOutput,
  SectionItemType,
  SectionItemInput,
} from "@/interfaces/section-items";
import { MenuGeneralSettings } from "./components/form/menu/MenuGeneralSettings";
import { OperatingHoursSettings } from "./components/form/menu/OperatingHours";
import { PaymentMethodsSettings } from "./components/form/menu/PaymentMethodsSettings";
import { SectionsList } from "./components/form/sections/SectionsList";
import { SectionModal } from "./components/form/modals/SectionModal";
import { DeleteConfirmationModal } from "../../components/DeleteConfirmationModal";
import { OfferModal } from "./components/form/modals/OfferModal";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAppManager } from "@/context/AppManagerContext";

const menusService = new MenusService();
const sectionsService = new SectionsService();
const offersService = new OffersService();
const sectionItemsService = new SectionItemsService();

interface ChangeTracker {
  sections: {
    created: SectionsOutput[];
    updated: SectionsOutput[];
    deleted: string[];
  };
  offers: {
    created: OffersOutput[];
    updated: OffersOutput[];
    deleted: string[];
  };
  sectionItems: {
    created: SectionItemOutput[];
    updated: SectionItemOutput[];
    deleted: string[];
  };
}

export default function MenusForm() {
  const params = useParams();
  const navigate = useNavigate();
  const menuId = params.id as string;
  const isNewMenu = !menuId;

  // Estados para armazenar dados
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [menu, setMenu] = useState<MenusOutput | null>(null);
  const [sections, setSections] = useState<SectionsOutput[]>([]);
  const [offers, setOffers] = useState<OffersOutput[]>([]);
  const [sectionItems, setSectionItems] = useState<SectionItemOutput[]>([]);

  // Rastreador de alterações
  const [changeTracker, setChangeTracker] = useState<ChangeTracker>({
    sections: { created: [], updated: [], deleted: [] },
    offers: { created: [], updated: [], deleted: [] },
    sectionItems: { created: [], updated: [], deleted: [] },
  });

  // Estados para modais
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState<SectionsOutput | null>(
    null
  );
  const [currentOffer, setCurrentOffer] = useState<OffersOutput | null>(null);
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
  const { currentOrganization } = useAppManager();

  // Configuração dos sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        if (isNewMenu) {
          const newMenu: MenusOutput = {
            id: "",
            name: "Novo Catálogo",
            slug: "",
            description: "",
            acceptDelivery: true,
            minDeliveryTime: 30,
            maxDeliveryTime: 60,
            operatingDays: [],
            maxDistance: 10,
            allowedPaymentMethods: [],
            minOrderPrice: 0,
            minDeliveryPrice: 0,
            maxDeliveryPrice: 0,
            kmTax: 0,
            isVisible: false,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          setMenu(newMenu);
          setSections([]);
          setOffers([]);
          setSectionItems([]);
        } else {
          const existingMenu = await menusService.findById(menuId);
          setMenu({
            ...existingMenu,
            acceptDelivery: existingMenu.acceptDelivery ?? true,
          });

          const menuSections =
            (await sectionsService.findByMenuId(menuId, {
              expand: ["file"],
            })) ?? [];
          setSections(menuSections);

          const allOffers: OffersOutput[] = [];
          const allSectionItems: SectionItemOutput[] = [];

          for (const section of menuSections) {
            // Carregar section items para cada seção com expand para pegar os items (offers e products)
            const items = await sectionItemsService.findBySectionId(
              section.id,
              {
                expand: ["items", "file"],
              }
            );
            allSectionItems.push(...items);

            // Extrair offers dos section items
            const sectionOffers = items
              .filter(
                (item: SectionItemOutput) =>
                  item.itemType === "OFFER" && item.offer
              )
              .map((item: SectionItemOutput) => item.offer!)
              .filter(Boolean);
            allOffers.push(...sectionOffers);
          }
          setOffers(allOffers);
          setSectionItems(allSectionItems);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [menuId, isNewMenu]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const reorderedSections = arrayMove(items, oldIndex, newIndex)?.map(
          (section, index) => ({
            ...section,
            position: index + 1,
          })
        );

        // Atualizar o change tracker
        const updatedIds = [items[oldIndex].id, items[newIndex].id];
        setChangeTracker((prev) => {
          const newTracker = { ...prev };

          updatedIds.forEach((id) => {
            const sectionToUpdate = reorderedSections.find((s) => s.id === id);
            if (!sectionToUpdate) return;

            if (newTracker.sections.created.some((s) => s.id === id)) {
              newTracker.sections.created = newTracker.sections.created?.map(
                (s) => (s.id === id ? sectionToUpdate : s)
              );
            } else {
              if (!newTracker.sections.updated.some((s) => s.id === id)) {
                newTracker.sections.updated.push(sectionToUpdate);
              } else {
                newTracker.sections.updated = newTracker.sections.updated?.map(
                  (s) => (s.id === id ? sectionToUpdate : s)
                );
              }
            }
          });

          return newTracker;
        });

        return reorderedSections;
      });
    }
  };

  const moveSection = (sectionId: string, direction: "up" | "down") => {
    const sectionIndex = sections.findIndex(
      (section) => section.id === sectionId
    );
    if (
      (direction === "up" && sectionIndex === 0) ||
      (direction === "down" && sectionIndex === sections.length - 1)
    ) {
      return;
    }

    const newSections = [...sections];
    const targetIndex =
      direction === "up" ? sectionIndex - 1 : sectionIndex + 1;
    const temp = newSections[sectionIndex];
    newSections[sectionIndex] = newSections[targetIndex];
    newSections[targetIndex] = temp;

    const updatedSections = newSections?.map((section, index) => ({
      ...section,
      position: index + 1,
    }));

    const updatedIds = [
      newSections[sectionIndex].id,
      newSections[targetIndex].id,
    ];

    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      updatedIds.forEach((id) => {
        if (!newTracker.sections.created.some((s) => s.id === id)) {
          if (!newTracker.sections.updated.some((s) => s.id === id)) {
            const sectionToUpdate = updatedSections.find((s) => s.id === id);
            if (sectionToUpdate) {
              newTracker.sections.updated.push(sectionToUpdate);
            }
          } else {
            newTracker.sections.updated = newTracker.sections.updated?.map(
              (s) =>
                s.id === id
                  ? updatedSections.find((us) => us.id === id) || s
                  : s
            );
          }
        } else {
          newTracker.sections.created = newTracker.sections.created?.map((s) =>
            s.id === id ? updatedSections.find((us) => us.id === id) || s : s
          );
        }
      });

      return newTracker;
    });

    setSections(updatedSections);
  };

  const moveOffer = (offerId: string, direction: "up" | "down") => {
    const offer = offers.find((o) => o.id === offerId);
    if (!offer) return;

    // Encontrar todas as ofertas da mesma seção e ordenar por posição
    const sectionOffers = offers
      .filter((o) => o.sectionId === offer.sectionId)
      .sort((a, b) => {
        // Garantir que a posição seja um número válido
        const posA = typeof a.position === "number" ? a.position : 999999;
        const posB = typeof b.position === "number" ? b.position : 999999;
        return posA - posB;
      });

    const offerIndex = sectionOffers.findIndex((o) => o.id === offerId);
    if (
      (direction === "up" && offerIndex === 0) ||
      (direction === "down" && offerIndex === sectionOffers.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === "up" ? offerIndex - 1 : offerIndex + 1;
    const targetOffer = sectionOffers[targetIndex];

    // Normalizar as posições para garantir consistência
    const normalizedOffers = sectionOffers.map((o, idx) => ({
      ...o,
      position: idx + 1,
    }));

    // Trocar as posições
    const updatedOffers = offers.map((o) => {
      // Encontrar a posição normalizada para esta oferta
      const normalizedOffer = normalizedOffers.find((no) => no.id === o.id);

      if (o.id === offerId) {
        return {
          ...o,
          position: normalizedOffer
            ? o.id === offerId
              ? targetIndex + 1
              : offerIndex + 1
            : o.position,
        };
      }
      if (o.id === targetOffer.id) {
        return {
          ...o,
          position: normalizedOffer
            ? o.id === targetOffer.id
              ? offerIndex + 1
              : targetIndex + 1
            : o.position,
        };
      }
      // Manter a posição normalizada para outras ofertas da mesma seção
      if (o.sectionId === offer.sectionId && normalizedOffer) {
        return { ...o, position: normalizedOffer.position };
      }
      return o;
    });

    setOffers(updatedOffers);

    // Atualizar o change tracker
    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      // Atualizar a oferta movida
      if (newTracker.offers.created.some((o) => o.id === offerId)) {
        newTracker.offers.created = newTracker.offers.created.map((o) =>
          o.id === offerId
            ? updatedOffers.find((uo) => uo.id === offerId) || o
            : o
        );
      } else {
        const updatedOffer = updatedOffers.find((o) => o.id === offerId);
        if (updatedOffer) {
          if (!newTracker.offers.updated.some((o) => o.id === offerId)) {
            newTracker.offers.updated.push(updatedOffer);
          } else {
            newTracker.offers.updated = newTracker.offers.updated.map((o) =>
              o.id === offerId ? updatedOffer : o
            );
          }
        }
      }

      // Atualizar a oferta alvo
      if (newTracker.offers.created.some((o) => o.id === targetOffer.id)) {
        newTracker.offers.created = newTracker.offers.created.map((o) =>
          o.id === targetOffer.id
            ? updatedOffers.find((uo) => uo.id === targetOffer.id) || o
            : o
        );
      } else {
        const updatedTargetOffer = updatedOffers.find(
          (o) => o.id === targetOffer.id
        );
        if (updatedTargetOffer) {
          if (!newTracker.offers.updated.some((o) => o.id === targetOffer.id)) {
            newTracker.offers.updated.push(updatedTargetOffer);
          } else {
            newTracker.offers.updated = newTracker.offers.updated.map((o) =>
              o.id === targetOffer.id ? updatedTargetOffer : o
            );
          }
        }
      }

      return newTracker;
    });
  };

  const toggleSectionVisibility = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;

    const updatedSection = { ...section, isVisible: !section.isVisible };

    setSections((prevSections) =>
      prevSections?.map((s) => (s.id === sectionId ? updatedSection : s))
    );

    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      if (newTracker.sections.created.some((s) => s.id === sectionId)) {
        newTracker.sections.created = newTracker.sections.created?.map((s) =>
          s.id === sectionId ? updatedSection : s
        );
      } else {
        if (!newTracker.sections.updated.some((s) => s.id === sectionId)) {
          newTracker.sections.updated.push(updatedSection);
        } else {
          newTracker.sections.updated = newTracker.sections.updated?.map((s) =>
            s.id === sectionId ? updatedSection : s
          );
        }
      }

      return newTracker;
    });
  };

  const openSectionModal = (section?: SectionsOutput) => {
    if (section) {
      setCurrentSection(section);
    } else {
      setCurrentSection({
        id: `temp-section-${Date.now()}`,
        position: sections?.length ?? 0 + 1,
        menuId: menu?.id || "",
        name: "",
        description: "",
        isVisible: true,
        organizationId: "org-1",
        offers: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
    setIsSectionModalOpen(true);
  };

  const saveSection = (section: SectionsOutput) => {
    const isNewSection = !sections?.some((s) => s.id === section.id);

    if (isNewSection) {
      setSections((prevSections) => [...(prevSections ?? []), section]);
    } else {
      setSections((prevSections) =>
        prevSections?.map((s) => (s.id === section.id ? section : s))
      );
    }

    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      if (isNewSection) {
        if (!newTracker.sections.created.some((s) => s.id === section.id)) {
          newTracker.sections.created.push(section);
        } else {
          newTracker.sections.created = newTracker.sections.created?.map((s) =>
            s.id === section.id ? section : s
          );
        }
      } else {
        if (!newTracker.sections.created.some((s) => s.id === section.id)) {
          if (!newTracker.sections.updated.some((s) => s.id === section.id)) {
            newTracker.sections.updated.push(section);
          } else {
            newTracker.sections.updated = newTracker.sections.updated?.map(
              (s) => (s.id === section.id ? section : s)
            );
          }
        } else {
          newTracker.sections.created = newTracker.sections.created?.map((s) =>
            s.id === section.id ? section : s
          );
        }
      }

      return newTracker;
    });

    setIsSectionModalOpen(false);
    setCurrentSection(null);
  };

  const confirmDeleteSection = (sectionId: string) => {
    setCurrentSectionId(sectionId);
    setIsDeleteModalOpen(true);
  };

  const deleteSection = () => {
    if (!currentSectionId) return;

    setSections((prevSections) =>
      prevSections?.filter((section) => section.id !== currentSectionId)
    );

    setOffers((prevOffers) =>
      prevOffers?.filter((offer) => offer.sectionId !== currentSectionId)
    );

    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      if (newTracker.sections.created.some((s) => s.id === currentSectionId)) {
        newTracker.sections.created = newTracker.sections.created?.filter(
          (s) => s.id !== currentSectionId
        );
      } else if (!newTracker.sections.deleted.includes(currentSectionId)) {
        newTracker.sections.deleted.push(currentSectionId);
        newTracker.sections.updated = newTracker.sections.updated?.filter(
          (s) => s.id !== currentSectionId
        );
      }

      const sectionOffers = offers?.filter(
        (o) => o.sectionId === currentSectionId
      );

      sectionOffers.forEach((offer) => {
        if (newTracker.offers.created.some((o) => o.id === offer.id)) {
          newTracker.offers.created = newTracker.offers.created?.filter(
            (o) => o.id !== offer.id
          );
        } else if (!newTracker.offers.deleted.includes(offer.id)) {
          newTracker.offers.deleted.push(offer.id);
          newTracker.offers.updated = newTracker.offers.updated?.filter(
            (o) => o.id !== offer.id
          );
        }
      });

      return newTracker;
    });

    setIsDeleteModalOpen(false);
    setCurrentSectionId(null);
  };

  const openOfferModal = (sectionId: string, offer?: OffersOutput) => {
    setCurrentSectionId(sectionId);
    if (offer) {
      setCurrentOffer(offer);
    } else {
      // Calcular a próxima posição para a nova oferta
      const sectionOffers = offers.filter((o) => o.sectionId === sectionId);
      const nextPosition =
        sectionOffers.length > 0
          ? Math.max(...sectionOffers.map((o) => o.position || 0)) + 1
          : 1;

      setCurrentOffer({
        id: `temp-offer-${Date.now()}`,
        sectionId: sectionId,
        name: "",
        description: "",
        isVisible: true,
        products: [],
        position: nextPosition,
        unitCost: 0,
        unitPrice: 0,
        organizationId: "org-1",
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
    setIsOfferModalOpen(true);
  };

  // Modifique a função saveOffer para garantir posições únicas ao adicionar novas ofertas
  // Substitua a função saveOffer existente com esta versão melhorada:

  const saveOffer = (offer: OffersOutput, product: OfferProducts) => {
    if (!currentSectionId) return;

    const updatedOffer = {
      ...offer,
      name: offer.name || product.name,
      description: offer.description || product.description,
      products: [product],
      unitCost: product.unitCost,
      unitPrice: product.unitPrice,
    };

    const isNewOffer = !offers.some((o) => o.id === updatedOffer.id);

    // Garantir posição única para a oferta dentro da seção
    const sectionOffers = offers.filter(
      (o) => o.sectionId === currentSectionId
    );

    if (isNewOffer) {
      // Para novas ofertas, encontrar a maior posição existente e adicionar 1
      const maxPosition =
        sectionOffers.length > 0
          ? Math.max(
              ...sectionOffers.map((o) =>
                typeof o.position === "number" ? o.position : 0
              )
            )
          : 0;

      updatedOffer.position = maxPosition + 1;

      setOffers((prevOffers) => [...prevOffers, updatedOffer]);
    } else {
      // Para ofertas existentes, manter a posição atual se válida
      if (
        typeof updatedOffer.position !== "number" ||
        isNaN(updatedOffer.position)
      ) {
        // Se a posição for inválida, encontrar uma posição disponível
        const usedPositions = new Set(sectionOffers.map((o) => o.position));
        let newPosition = 1;
        while (usedPositions.has(newPosition)) {
          newPosition++;
        }
        updatedOffer.position = newPosition;
      }

      setOffers((prevOffers) =>
        prevOffers?.map((o) => (o.id === updatedOffer.id ? updatedOffer : o))
      );
    }

    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      if (isNewOffer) {
        if (!newTracker.offers.created.some((o) => o.id === updatedOffer.id)) {
          newTracker.offers.created.push(updatedOffer);
        } else {
          newTracker.offers.created = newTracker.offers.created?.map((o) =>
            o.id === updatedOffer.id ? updatedOffer : o
          );
        }
      } else {
        if (!newTracker.offers.created.some((o) => o.id === updatedOffer.id)) {
          if (
            !newTracker.offers.updated.some((o) => o.id === updatedOffer.id)
          ) {
            newTracker.offers.updated.push(updatedOffer);
          } else {
            newTracker.offers.updated = newTracker.offers.updated?.map((o) =>
              o.id === updatedOffer.id ? updatedOffer : o
            );
          }
        } else {
          newTracker.offers.created = newTracker.offers.created?.map((o) =>
            o.id === updatedOffer.id ? updatedOffer : o
          );
        }
      }

      return newTracker;
    });

    setIsOfferModalOpen(false);
    setCurrentOffer(null);
    setCurrentSectionId(null);
  };

  const toggleOfferVisibility = (offerId: string) => {
    const offer = offers.find((o) => o.id === offerId);
    if (!offer) return;

    const updatedOffer = { ...offer, isVisible: !offer.isVisible };

    setOffers((prevOffers) =>
      prevOffers?.map((o) => (o.id === offerId ? updatedOffer : o))
    );

    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      if (newTracker.offers.created.some((o) => o.id === offerId)) {
        newTracker.offers.created = newTracker.offers.created?.map((o) =>
          o.id === offerId ? updatedOffer : o
        );
      } else {
        if (!newTracker.offers.updated.some((o) => o.id === offerId)) {
          newTracker.offers.updated.push(updatedOffer);
        } else {
          newTracker.offers.updated = newTracker.offers.updated?.map((o) =>
            o.id === offerId ? updatedOffer : o
          );
        }
      }

      return newTracker;
    });
  };

  const deleteOffer = (offerId: string) => {
    setOffers((prevOffers) =>
      prevOffers?.filter((offer) => offer.id !== offerId)
    );

    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      if (newTracker.offers.created.some((o) => o.id === offerId)) {
        newTracker.offers.created = newTracker.offers.created?.filter(
          (o) => o.id !== offerId
        );
      } else if (!newTracker.offers.deleted.includes(offerId)) {
        newTracker.offers.deleted.push(offerId);
        newTracker.offers.updated = newTracker.offers.updated?.filter(
          (o) => o.id !== offerId
        );
      }

      return newTracker;
    });
  };

  // Funções para gerenciar Section Items
  const addSectionItem = (
    sectionId: string,
    itemId: string,
    itemType: SectionItemType,
    details?: ProductsOutput | OffersOutput
  ) => {
    // Calcular a próxima posição
    const sectionItemsForSection = sectionItems.filter(
      (item) => item.sectionId === sectionId
    );
    const nextPosition =
      sectionItemsForSection.length > 0
        ? Math.max(
            ...sectionItemsForSection.map((item) => item.position || 0)
          ) + 1
        : 1;

    const newSectionItem: SectionItemOutput = {
      id: `temp-section-item-${Date.now()}`,
      sectionId,
      itemId,
      itemType,
      position: nextPosition,
      isVisible: true,
      organizationId: currentOrganization?.id || "",
      createdAt: new Date(),
      updatedAt: new Date(),
      // Hidratar imediatamente para evitar skeleton infinito
      ...(itemType === "PRODUCT"
        ? { product: details as ProductsOutput | undefined }
        : { offer: details as OffersOutput | undefined }),
    };

    setSectionItems((prev) => [...prev, newSectionItem]);

    setChangeTracker((prev) => ({
      ...prev,
      sectionItems: {
        ...prev.sectionItems,
        created: [...prev.sectionItems.created, newSectionItem],
      },
    }));
  };

  const toggleSectionItemVisibility = (itemId: string) => {
    setSectionItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, isVisible: !item.isVisible } : item
      )
    );

    setChangeTracker((prev) => {
      const newTracker = { ...prev };
      const item = sectionItems.find((i) => i.id === itemId);

      if (item) {
        if (!newTracker.sectionItems.created.some((i) => i.id === itemId)) {
          const updatedItem = { ...item, isVisible: !item.isVisible };
          if (!newTracker.sectionItems.updated.some((i) => i.id === itemId)) {
            newTracker.sectionItems.updated.push(updatedItem);
          } else {
            newTracker.sectionItems.updated =
              newTracker.sectionItems.updated.map((i) =>
                i.id === itemId ? updatedItem : i
              );
          }
        } else {
          newTracker.sectionItems.created = newTracker.sectionItems.created.map(
            (i) => (i.id === itemId ? { ...i, isVisible: !i.isVisible } : i)
          );
        }
      }

      return newTracker;
    });
  };

  const deleteSectionItem = (itemId: string) => {
    setSectionItems((prev) => prev.filter((item) => item.id !== itemId));

    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      if (newTracker.sectionItems.created.some((i) => i.id === itemId)) {
        newTracker.sectionItems.created =
          newTracker.sectionItems.created.filter((i) => i.id !== itemId);
      } else if (!newTracker.sectionItems.deleted.includes(itemId)) {
        newTracker.sectionItems.deleted.push(itemId);
        newTracker.sectionItems.updated =
          newTracker.sectionItems.updated.filter((i) => i.id !== itemId);
      }

      return newTracker;
    });
  };

  const moveSectionItem = (itemId: string, direction: "up" | "down") => {
    const item = sectionItems.find((i) => i.id === itemId);
    if (!item) return;

    // Encontrar todos os itens da mesma seção e ordenar por posição
    const sectionItemsForSection = sectionItems
      .filter((i) => i.sectionId === item.sectionId)
      .sort((a, b) => {
        const posA = typeof a.position === "number" ? a.position : 999999;
        const posB = typeof b.position === "number" ? b.position : 999999;
        return posA - posB;
      });

    const itemIndex = sectionItemsForSection.findIndex((i) => i.id === itemId);
    if (
      (direction === "up" && itemIndex === 0) ||
      (direction === "down" && itemIndex === sectionItemsForSection.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === "up" ? itemIndex - 1 : itemIndex + 1;
    const targetItem = sectionItemsForSection[targetIndex];

    // Trocar as posições
    const updatedSectionItems = sectionItems.map((i) => {
      if (i.id === itemId) {
        return { ...i, position: targetItem.position };
      }
      if (i.id === targetItem.id) {
        return { ...i, position: item.position };
      }
      return i;
    });

    setSectionItems(updatedSectionItems);

    // Atualizar o change tracker
    setChangeTracker((prev) => {
      const newTracker = { ...prev };

      [itemId, targetItem.id].forEach((id) => {
        const updatedItem = updatedSectionItems.find((i) => i.id === id);
        if (!updatedItem) return;

        if (!newTracker.sectionItems.created.some((i) => i.id === id)) {
          if (!newTracker.sectionItems.updated.some((i) => i.id === id)) {
            newTracker.sectionItems.updated.push(updatedItem);
          } else {
            newTracker.sectionItems.updated =
              newTracker.sectionItems.updated.map((i) =>
                i.id === id ? updatedItem : i
              );
          }
        } else {
          newTracker.sectionItems.created = newTracker.sectionItems.created.map(
            (i) => (i.id === id ? updatedItem : i)
          );
        }
      });

      return newTracker;
    });
  };

  const toggleMenuVisibility = () => {
    if (!menu) return;

    setMenu({
      ...menu,
      isVisible: !menu.isVisible,
    });
  };

  const saveAllChanges = async () => {
    if (!menu) return;

    try {
      setIsSaving(true);

      let savedMenuId = menu.id;

      if (isNewMenu) {
        if (!menu.name.trim()) {
          setIsSaving(false);
          return;
        }

        const newMenu = await menusService.create(menu);
        savedMenuId = newMenu.id;

        setMenu(newMenu);

        setSections((prevSections) =>
          prevSections?.map((section) => ({
            ...section,
            menuId: savedMenuId,
          }))
        );

        setChangeTracker((prev) => {
          const newTracker = { ...prev };

          newTracker.sections.created = newTracker.sections.created?.map(
            (section) => ({
              ...section,
              menuId: savedMenuId,
            })
          );

          newTracker.sections.updated = newTracker.sections.updated?.map(
            (section) => ({
              ...section,
              menuId: savedMenuId,
            })
          );

          return newTracker;
        });
      } else {
        await menusService.update(menu.id, menu);
      }

      for (const sectionId of changeTracker.sections.deleted) {
        await sectionsService.delete(sectionId);
      }

      for (const offerId of changeTracker.offers.deleted) {
        await offersService.delete(offerId);
      }

      const sectionsIdsMap = new Map();

      for (const section of changeTracker.sections.created) {
        const sectionToCreate = {
          ...section,
          menuId: savedMenuId,
        };

        const tempId = sectionToCreate.id;
        if (sectionToCreate.id.startsWith("temp-")) {
          delete (sectionToCreate as { id?: string }).id;
        }

        const createdSection = await sectionsService.create(sectionToCreate);
        sectionsIdsMap.set(tempId, createdSection.id);
      }

      for (const section of changeTracker.sections.updated) {
        await sectionsService.update(section.id, section);
      }

      // Normalizar posições por seção antes de salvar
      const sectionOffersMap = new Map<string, OffersOutput[]>();

      // Agrupar ofertas por seção
      for (const offer of [
        ...changeTracker.offers.created,
        ...changeTracker.offers.updated,
      ]) {
        const sectionId = offer.sectionId;
        if (!sectionId) continue;
        if (!sectionOffersMap.has(sectionId)) {
          sectionOffersMap.set(sectionId, []);
        }
        sectionOffersMap.get(sectionId)?.push(offer);
      }

      // Normalizar posições dentro de cada seção
      sectionOffersMap.forEach((sectionOffers) => {
        // Ordenar por posição atual
        const sortedOffers = [...sectionOffers].sort((a, b) => {
          const posA = typeof a.position === "number" ? a.position : 999999;
          const posB = typeof b.position === "number" ? b.position : 999999;
          return posA - posB;
        });

        // Atribuir novas posições sequenciais
        sortedOffers.forEach((offer, index) => {
          offer.position = index + 1;
        });
      });

      // Agora salvar as ofertas com posições normalizadas
      for (const offer of changeTracker.offers.created) {
        const realSectionId =
          sectionsIdsMap.get(offer.sectionId) || offer.sectionId;

        const offerToCreate = {
          ...offer,
          sectionId: realSectionId,
        };

        if (offerToCreate.id.startsWith("temp-")) {
          delete (offerToCreate as { id?: string }).id;
        }

        await offersService.create({
          ...offerToCreate,
          products: offer.products?.map((p) => p.productId),
        });
      }

      for (const offer of changeTracker.offers.updated) {
        await offersService.update(offer.id, {
          ...offer,
          products: offer.products?.map((p) => ({
            product_id: p.productId,
            quantity: p.quantity || 1,
          })),
        });
      }

      // Processar section items deletados
      for (const sectionItemId of changeTracker.sectionItems.deleted) {
        await sectionItemsService.delete(sectionItemId);
      }

      // Processar section items criados
      for (const sectionItem of changeTracker.sectionItems.created) {
        // Construir payload explicitamente para evitar 'any' e campos expandidos
        const itemToCreate: SectionItemInput & { id?: string } = {
          id: sectionItem.id,
          sectionId: sectionItem.sectionId,
          itemId: sectionItem.itemId,
          itemType: sectionItem.itemType,
          position: sectionItem.position,
          isVisible: sectionItem.isVisible,
        };

        // Mapear sectionId para novo ID se necessário
        if (sectionsIdsMap.has(itemToCreate.sectionId)) {
          const mapped = sectionsIdsMap.get(itemToCreate.sectionId);
          if (mapped) itemToCreate.sectionId = mapped as string;
        }

        if (
          typeof itemToCreate.id === "string" &&
          itemToCreate.id.startsWith("temp-")
        ) {
          delete itemToCreate.id;
        }

        await sectionItemsService.create(itemToCreate);
      }

      // Processar section items atualizados
      for (const sectionItem of changeTracker.sectionItems.updated) {
        await sectionItemsService.update(sectionItem.id, sectionItem);
      }

      setChangeTracker({
        sections: { created: [], updated: [], deleted: [] },
        offers: { created: [], updated: [], deleted: [] },
        sectionItems: { created: [], updated: [], deleted: [] },
      });

      if (isNewMenu) {
        navigate(`/catalogos/${savedMenuId}/editar`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !menu) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-500">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!menu) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-md">
          <h1 className="text-2xl font-bold mb-4">
            Nenhum catálogo encontrado
          </h1>
          <p className="text-gray-500 mb-6">
            Não foi possível encontrar um catálogo para editar.
          </p>
          <Button onClick={() => navigate("/catalogos/novo")}>
            Criar novo catálogo
          </Button>
        </div>
      </div>
    );
  }

  const hasValidId = menuId && menuId.length > 0 && !menuId.startsWith("temp-");
  const catalogUrl = `${
    import.meta.env.VITE_CATALOG_BASE_URL || "https://catalogos.pedidoz.online"
  }/${currentOrganization?.slug}/${menu.slug}`;

  return (
    <div className="min-h-screen bg-gray-50/30 pb-20">
      {/* Header fixo */}
      <Card className="sticky top-0 z-10 border-b shadow-lg rounded-none">
        <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-white text-xl font-semibold">
                {isNewMenu ? "Novo Catálogo" : "Editar Catálogo"}
              </CardTitle>
              {!menu.isVisible && (
                <Badge
                  variant="secondary"
                  className="bg-white/20 text-white border-none flex items-center gap-1"
                >
                  <EyeOff className="h-3 w-3" />
                  Menu Oculto
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              {hasValidId && (
                <Button
                  variant="outline"
                  className="bg-white/95 text-[#EE7B11] hover:bg-white hover:text-[#d06a0a] border-white/50"
                  onClick={() => window.open(catalogUrl, "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ver Catálogo Online
                </Button>
              )}
              <Button
                onClick={saveAllChanges}
                disabled={isSaving}
                className="bg-white text-[#EE7B11] hover:bg-gray-100 hover:text-[#d06a0a] font-medium shadow-sm"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <main className="container mx-auto p-4 space-y-4 mt-2">
        {/* Configurações Gerais */}
        <Card className="border shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b rounded-t-lg py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#EE7B11] rounded-lg">
                  <Settings className="h-5 w-5 text-white" />
                </div>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Configurações Gerais
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="menu-visible"
                  checked={menu.isVisible}
                  onCheckedChange={toggleMenuVisibility}
                />
                <Label
                  htmlFor="menu-visible"
                  className="text-sm font-medium cursor-pointer"
                >
                  {menu.isVisible ? "Menu Visível" : "Menu Oculto"}
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            <MenuGeneralSettings menu={menu} onMenuChange={setMenu} />
          </CardContent>
        </Card>

        {/* Métodos de Pagamento */}
        <Card className="border shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b rounded-t-lg py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#EE7B11] rounded-lg">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Métodos de Pagamento
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            <PaymentMethodsSettings menu={menu} onMenuChange={setMenu} />
          </CardContent>
        </Card>

        {/* Horários de Funcionamento */}
        <Card className="border shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b rounded-t-lg py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#EE7B11] rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Horários de Funcionamento
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            <OperatingHoursSettings menu={menu} onMenuChange={setMenu} />
          </CardContent>
        </Card>

        {/* Seções do Catálogo */}
        <Card className="border shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b rounded-t-lg py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#EE7B11] rounded-lg">
                  <Layers className="h-5 w-5 text-white" />
                </div>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Seções do Catálogo
                </CardTitle>
              </div>
              <Button
                onClick={() => openSectionModal()}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white font-medium shadow-sm"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Seção
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={sections?.map((section) => section.id)}
                strategy={verticalListSortingStrategy}
              >
                <SectionsList
                  sections={sections}
                  offers={offers}
                  sectionItems={sectionItems}
                  onAddSection={() => openSectionModal()}
                  onMoveSection={moveSection}
                  onToggleSectionVisibility={toggleSectionVisibility}
                  onEditSection={openSectionModal}
                  onDeleteSection={confirmDeleteSection}
                  onAddOffer={openOfferModal}
                  onToggleOfferVisibility={toggleOfferVisibility}
                  onDeleteOffer={deleteOffer}
                  onMoveOffer={moveOffer}
                  onAddSectionItem={addSectionItem}
                  onToggleSectionItemVisibility={toggleSectionItemVisibility}
                  onDeleteSectionItem={deleteSectionItem}
                  onMoveSectionItem={moveSectionItem}
                />
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-md p-4 flex justify-end">
        <Button
          onClick={saveAllChanges}
          disabled={isSaving}
          size="lg"
          className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
        >
          <Save className="h-5 w-5 mr-2" />
          {isSaving ? "Salvando..." : "Salvar Alterações"}
        </Button>
      </div>

      <SectionModal
        isOpen={isSectionModalOpen}
        onClose={() => setIsSectionModalOpen(false)}
        onSave={saveSection}
        section={currentSection}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteSection}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir esta seção? Todos os produtos associados a ela também serão removidos."
      />

      <OfferModal
        isOpen={isOfferModalOpen}
        onClose={() => {
          setIsOfferModalOpen(false);
          setCurrentOffer(null);
          setCurrentSectionId(null);
        }}
        onSave={saveOffer}
        offer={currentOffer}
        sectionId={currentSectionId}
      />
    </div>
  );
}
